import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { getQuizResultDetails } from '../services/quizService';
import { DetailedQuizResult, DifficultyLevel } from '../types';
import { QuestionFlagModal } from '../components/QuestionFlagModal';
import { QuestionDisplay } from '../components/QuestionDisplay';
import {
  Card,
  CardBody,
  Section,
  Button,
  Badge
} from '../components/BaseComponents';

/**
 * QuizReviewPage Component
 * 
 * Displays detailed results for a specific completed quiz.
 * Features:
 * - Complete quiz summary with score and timing
 * - Question-by-question breakdown with user answers
 * - Correct answers and explanations
 * - Performance insights and feedback
 * - Navigation back to quiz history
 */
export const QuizReviewPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useUser();

  const [quizResult, setQuizResult] = useState<DetailedQuizResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Flag modal state
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [selectedQuestionForFlag, setSelectedQuestionForFlag] = useState<any | null>(null);
  const [flagSuccessMessage, setFlagSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (attemptId && currentUser) {
      loadQuizResults();
    }
  }, [attemptId, currentUser]);

  const loadQuizResults = async () => {
    if (!attemptId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getQuizResultDetails(attemptId);
      setQuizResult(result);
    } catch (err) {
      console.error('Error loading quiz results:', err);
      setError('Failed to load quiz results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format duration for display
  const formatDuration = (minutes?: number): string => {
    if (minutes === null || minutes === undefined) return 'Unknown';
    if (minutes === 0) return `1m`;
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Format date for display
  const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get difficulty badge color
  const getDifficultyColor = (difficulty: DifficultyLevel): string => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get score badge color based on percentage
  const getScoreColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-green-100 text-green-800';
    if (percentage >= 70) return 'bg-blue-100 text-blue-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  // Get performance message based on score
  const getPerformanceMessage = (percentage: number): { message: string; color: string } => {
    if (percentage >= 90) {
      return { message: 'Excellent work! You have mastered this material.', color: 'text-green-700' };
    } else if (percentage >= 70) {
      return { message: 'Good job! You have a solid understanding.', color: 'text-blue-700' };
    } else if (percentage >= 50) {
      return { message: 'Not bad! There is room for improvement.', color: 'text-yellow-700' };
    } else {
      return { message: 'Keep practicing! Review the material and try again.', color: 'text-red-700' };
    }
  };


  // Flag question handlers
  const handleFlagQuestion = (questionResult: any) => {
    setSelectedQuestionForFlag(questionResult);
    setFlagModalOpen(true);
  };

  const handleFlagSuccess = () => {
    setFlagSuccessMessage('Question has been flagged for review. Thank you for your feedback!');
    setTimeout(() => {
      setFlagSuccessMessage(null);
    }, 5000);
  };

  const handleCloseFlagModal = () => {
    setFlagModalOpen(false);
    setSelectedQuestionForFlag(null);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Please log in to view quiz results.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8 px-4">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading quiz results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8 px-4">
          <Section>
            <Card className="border-red-200 bg-red-50">
              <CardBody>
                <div className="flex items-center justify-between">
                  <p className="text-red-700">{error}</p>
                  <div className="space-x-3">
                    <Button onClick={loadQuizResults} variant="outline" size="sm">
                      Try Again
                    </Button>
                    <Button onClick={() => navigate('/history')} variant="secondary" size="sm">
                      Back to History
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Section>
        </div>
      </div>
    );
  }

  if (!quizResult) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8 px-4">
          <Section>
            <Card>
              <CardBody className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Quiz Not Found</h3>
                <p className="text-gray-600 mb-6">
                  The quiz results you're looking for could not be found.
                </p>
                <Button onClick={() => navigate('/history')} variant="primary">
                  Back to History
                </Button>
              </CardBody>
            </Card>
          </Section>
        </div>
      </div>
    );
  }

  const performanceMessage = getPerformanceMessage(quizResult.scorePercentage);
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <Section className="section-spacing">
          <Card className="card-hero" noHover={true}>
            <CardBody>
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/history')}
                  className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                >
                  ← Back to History
                </Button>
                <div className="text-right">
                  <div className="text-sm text-indigo-200">Quiz Completed</div>
                  <div className="text-lg">{formatDate(quizResult.completedAt!)}</div>
                </div>
              </div>

              <h1 className="text-page-title">Quiz Results</h1>
              <p className="text-indigo-100">
                {quizResult.source?.displayName || 'General Quiz'}
              </p>
            </CardBody>
          </Card>
        </Section>

        {/* Quiz Summary */}
        <Section className="section-spacing">
          <Card>
            <CardBody>
              <h2 className="text-card-title">Summary</h2>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${getScoreColor(quizResult.scorePercentage).includes('green') ? 'text-green-600' :
                    getScoreColor(quizResult.scorePercentage).includes('blue') ? 'text-blue-600' :
                      getScoreColor(quizResult.scorePercentage).includes('yellow') ? 'text-yellow-600' : 'text-red-600'}`}>
                    {quizResult.scorePercentage}%
                  </div>
                  <div className="text-sm text-gray-600">Score</div>
                </div>

                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-700">
                    {quizResult.correctAnswers}/{quizResult.totalQuestions}
                  </div>
                  <div className="text-sm text-gray-600">Correct</div>
                </div>

                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-700">
                    {formatDuration(quizResult.duration)}
                  </div>
                  <div className="text-sm text-gray-600">Duration</div>
                </div>

                <div className="text-center">
                  <Badge className={getDifficultyColor(quizResult.difficulty)}>
                    {quizResult.difficulty.charAt(0).toUpperCase() + quizResult.difficulty.slice(1)}
                  </Badge>
                  <div className="text-sm text-gray-600 mt-1">Difficulty</div>
                </div>
              </div>

              <div className={`text-center p-4 rounded-lg bg-gray-50 ${performanceMessage.color}`}>
                <p className="font-medium">{performanceMessage.message}</p>
              </div>

              {/* Success message for question flagging */}
              {flagSuccessMessage && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-green-700">{flagSuccessMessage}</p>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Section>

        {/* Question Results */}
        <Section>
          <Card noHover>
            <CardBody>
              <h2 className="text-card-title mb-6">Question by Question</h2>

              <div className="space-y-6">
                {quizResult.questionResults.map((result, index) => (
                  <div key={result.questionId} className="space-y-4">
                    {/* Question header with result indicator */}
                    <div className={`p-4 rounded-lg border-2 ${result.isCorrect
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                      }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${result.isCorrect ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                            {index + 1}
                          </div>
                          <Badge className={result.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {result.isCorrect ? 'Correct' : 'Incorrect'}
                          </Badge>
                          <Badge className="bg-gray-100 text-gray-800 text-xs">
                            {result.question.difficulty}
                          </Badge>
                        </div>

                        {result.answeredAt && (
                          <div className="text-sm text-gray-500">
                            Answered: {formatDate(result.answeredAt)}
                          </div>
                        )}
                      </div>

                      <QuestionDisplay
                        questionResult={result}
                        userAnswer={result.userAnswer || ''}
                        onAnswerSubmit={() => { }} // No-op in review mode
                        showResult={true}
                        onMoveToNext={undefined}
                        shuffleSeed={attemptId}
                      />

                      {/* Flag question button */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-600">
                            Think there's an issue with this question?
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFlagQuestion(result)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.707 1.707L13.414 9l3.293 3.293A1 1 0 0116 14H4a1 1 0 01-1-1V5zm3.293 4.293a1 1 0 000 1.414L8.586 13h6.828l-1.707-1.707a1 1 0 000-1.414L15.414 8H8.586l1.707 1.707z" clipRule="evenodd" />
                            </svg>
                            Flag for Review
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </Section>

        {/* Action Buttons */}
        <Section className="mt-8">
          <Card>
            <CardBody>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/history')}
                >
                  Back to History
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/categories')}
                >
                  Take Another Quiz
                </Button>
              </div>
            </CardBody>
          </Card>
        </Section>
      </div>

      {/* Question Flag Modal */}
      {selectedQuestionForFlag && (
        <QuestionFlagModal
          isOpen={flagModalOpen}
          onClose={handleCloseFlagModal}
          question={selectedQuestionForFlag.question}
          onSuccess={handleFlagSuccess}
          shuffleSeed={attemptId}
        />
      )}
    </div>
  );
};

export default QuizReviewPage;